import { useRef } from "react";
import {
  TabGroup,
  Tab,
  EmailEditor,
  SmsEditor,
  InvoiceDropdown,
  ChannelDropdown,
  PdfDropdown,
  PaymentProfilesDropdown,
  OpenRequestPreview,
} from "@components";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { cn } from "@utils";
import { useAvailableHeight } from "@hooks";
import { usePaymentProfilesContext } from "@context/PaymentProfilesContext";
import EmailPreview from "./EmailPreview";
import SmsPreview from "./SmsPreview";
import ScaledPreview from "./ScaledPreview";
import EmailFormattingToolbar from "./EmailFormattingToolbar";

export default function TemplateEditorPanel({
  control,
  errors,
  watch,
  getValues,
  setValue,
  merchant,
  activeTab,
  setActiveTab,
  emailEnabled,
  smsEnabled,
  previewMode,
  onToggleChannel,
  onEnterEdit,
  disableCustom = false,
  dropdownEditBadges,
  previewTokens,
  previewCustomerSelector,
  previewCustomerRow,
  invoicesRequested,
  mandatory,
  renderFooter,
  formProps,
  showEmptyState = true,
  onSetPreviewMode,
}) {
  const { contentRef, availableHeight } = useAvailableHeight();
  const { profiles: paymentProfiles } = usePaymentProfilesContext();
  const titleRef = useRef(null);
  const mainTextRef = useRef(null);

  const toolbar = (
    <div className="border-b border-zinc-200 bg-white">
      <div className="flex items-center">
        <div className="min-w-0 flex flex-wrap gap-2 px-4 py-3 flex-1">
          <InvoiceDropdown
            control={control}
            watch={watch}
            setValue={setValue}
            variant="pill"
            edited={dropdownEditBadges?.invoice}
            disabled={previewMode}
            disableCustom={disableCustom}
          />
          {emailEnabled && (
            <PdfDropdown
              control={control}
              watch={watch}
              variant="pill"
              edited={dropdownEditBadges?.pdf}
              disabled={previewMode}
            />
          )}
          <ChannelDropdown
            emailEnabled={emailEnabled}
            smsEnabled={smsEnabled}
            onToggle={onToggleChannel}
            variant="pill"
            edited={dropdownEditBadges?.channel}
            disabled={previewMode}
          />
          <PaymentProfilesDropdown
            control={control}
            watch={watch}
            paymentProfiles={paymentProfiles}
            variant="pill"
            edited={dropdownEditBadges?.paymentProfile}
            disabled={previewMode}
          />
        </div>
        {onSetPreviewMode && (
          <div className="shrink-0 flex items-center justify-center pr-4">
            <TabGroup className="my-0 w-44">
              <Tab active={!previewMode} onClick={() => onSetPreviewMode(false)}>
                Edit
              </Tab>
              <Tab active={previewMode} onClick={() => onSetPreviewMode(true)}>
                Preview
              </Tab>
            </TabGroup>
          </div>
        )}
        {onEnterEdit && previewMode && !onSetPreviewMode && (
          <button
            className="py-3 px-4 h-full flex items-center justify-center text-primary shrink-0"
            onClick={onEnterEdit}
          >
            <PencilSquareIcon className="size-5 " />
          </button>
        )}
      </div>
      {!previewMode && emailEnabled && activeTab === "email" && (
        <div className="flex items-center px-4 py-2 border-t border-zinc-100">
          <EmailFormattingToolbar editorRefs={[titleRef, mainTextRef]} />
        </div>
      )}
    </div>
  );

  const values = previewMode ? getValues() : undefined;

  const content = (
    <div
      className={cn(
        "relative flex-1 min-h-0 overflow-y-auto",
        previewMode && "flex flex-col",
      )}
    >
      {previewMode ? (
        <>
          {previewCustomerSelector}
          <div
            ref={contentRef}
            className={cn(
              "flex-1 min-h-0 flex items-center justify-center gap-10 px-4 overflow-hidden",
            )}
          >
            {emailEnabled && (
              <ScaledPreview availableHeight={availableHeight} label="Email">
                <EmailPreview
                  values={values}
                  merchant={merchant}
                  previewTokens={previewTokens}
                />
              </ScaledPreview>
            )}
            {smsEnabled && (
              <ScaledPreview availableHeight={availableHeight} label="SMS">
                <SmsPreview
                  values={values}
                  merchant={merchant}
                  previewTokens={previewTokens}
                />
              </ScaledPreview>
            )}
            <ScaledPreview
              availableHeight={availableHeight}
              label="Payment link"
            >
              <OpenRequestPreview
                merchant={merchant}
                customerRow={previewCustomerRow}
                invoicesRequested={invoicesRequested}
                mandatory={mandatory}
                previewTokens={previewTokens}
              />
            </ScaledPreview>
            {!emailEnabled && !smsEnabled && showEmptyState && (
              <div className="flex-1 flex items-center justify-center text-sm text-zinc-400">
                No channels selected
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="z-0 flex flex-col items-center pt-3 pointer-events-none top-0 gap-4">
            {emailEnabled && smsEnabled && (
              <div className="flex items-center gap-2">
                <TabGroup
                  className="my-0 pointer-events-auto shadow-sm rounded-full"
                  variant="pill"
                >
                  <Tab
                    active={activeTab === "email"}
                    onClick={() => setActiveTab("email")}
                    variant="pill"
                  >
                    Email
                  </Tab>
                  <Tab
                    active={activeTab === "sms"}
                    onClick={() => setActiveTab("sms")}
                    variant="pill"
                  >
                    SMS
                  </Tab>
                </TabGroup>
              </div>
            )}
            <div>
              <span className="text-xs text-blue-800">
                <strong>To edit:</strong> Click anywhere inside the template
                below to type or insert a new field
              </span>
            </div>
          </div>

          {activeTab === "email" && emailEnabled && (
            <EmailEditor
              control={control}
              errors={errors}
              merchant={merchant}
              setValue={setValue}
              titleRef={titleRef}
              mainTextRef={mainTextRef}
            />
          )}

          {activeTab === "sms" && smsEnabled && (
            <SmsEditor control={control} errors={errors} />
          )}

          {showEmptyState && !emailEnabled && !smsEnabled && (
            <div className="flex-1 flex items-center justify-center text-sm text-zinc-400">
              No channels selected
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      {formProps ? (
        <form
          onSubmit={formProps.onSubmit}
          className="flex flex-col flex-1 min-h-0"
        >
          {toolbar}
          {content}
        </form>
      ) : (
        <>
          {toolbar}
          {content}
        </>
      )}
      {renderFooter?.({ previewMode, availableHeight })}
    </>
  );
}
